import { sendTo, setup } from 'xstate';

import type {
	OrchestrationActions,
	OrchestrationActors,
	OrchestrationChildren,
	OrchestrationContext,
	OrchestrationEvents,
	OrchestrationGuards,
} from './types';
import { assertJobStart } from './utils';

export const machine = setup({
	/* oxlint-disable typescript/no-unsafe-type-assertion, typescript/no-unnecessary-type-assertion -- xstate setup() infers context/input/events from the {} literal; the cast is how these get typed at all */
	types: {
		context: {} as OrchestrationContext,
		events: {} as OrchestrationEvents,
		children: {} as OrchestrationChildren,
	},
	actions: {} as OrchestrationActions,
	guards: {} as OrchestrationGuards,
	actors: {} as OrchestrationActors,
	/* oxlint-enable typescript/no-unsafe-type-assertion, typescript/no-unnecessary-type-assertion */
}).createMachine({
	id: 'start-a-petition',
	type: 'parallel',
	context: ({ input }) => ({ ...input }),
	states: {
		// Variant selection; the step graph lives in the invoked variant actor. Its value never
		// changes after the initial pick.
		flow: {
			initial: 'idle',
			states: {
				idle: {
					always: [
						// place all variants before control, as control is the default fallback
						// if no guards match, fallback to control
						{ target: 'control' },
					],
				},

				control: {
					invoke: {
						id: 'controlActorId',
						src: 'controlActorSrc',
						input: (input) => input.context,
					},
					on: {
						submit: {
							actions: sendTo('controlActorId', ({ event }) => event),
						},
						back: {
							actions: sendTo('controlActorId', ({ event }) => event),
						},
						error: {
							actions: sendTo('controlActorId', ({ event }) => event),
						},
						patch: {
							actions: sendTo('controlActorId', ({ event }) => event),
						},
						'job.done': {
							actions: sendTo('controlActorId', ({ event }) => event),
						},
					},
				},
			},
		},

		// Background operations, shared by every variant. A variant starts one with `job.start`; on
		// success the region raises `job.done`, which the active variant receives through its relay
		// row and applies to the draft it owns. A failure only moves the region to `failed`.
		// `done` and `failed` handle no `job.start`, so an operation runs at most once per machine.
		jobs: {
			type: 'parallel',
			states: {
				inferScopeLocation: {
					initial: 'idle',
					states: {
						idle: {
							on: {
								'job.start': { target: 'running', guard: 'canStartInferScopeLocation' },
							},
						},
						running: {
							invoke: {
								id: 'inferScopeLocationActorId',
								src: 'inferScopeLocationSrc',
								input: ({ event }) => {
									assertJobStart(event, 'inferScopeLocation');
									return event.input;
								},
								onDone: {
									target: 'done',
									actions: {
										type: 'raiseJobDone',
										params: ({ event }) => ({ name: 'inferScopeLocation', output: event.output }),
									},
								},
								onError: { target: 'failed' },
							},
						},
						done: {},
						failed: {},
					},
				},
				inferDecisionMakers: {
					initial: 'idle',
					states: {
						idle: {
							on: {
								'job.start': { target: 'running', guard: 'canStartInferDecisionMakers' },
							},
						},
						running: {
							invoke: {
								id: 'inferDecisionMakersActorId',
								src: 'inferDecisionMakersSrc',
								input: ({ event }) => {
									assertJobStart(event, 'inferDecisionMakers');
									return event.input;
								},
								onDone: {
									target: 'done',
									actions: {
										type: 'raiseJobDone',
										params: ({ event }) => ({ name: 'inferDecisionMakers', output: event.output }),
									},
								},
								onError: { target: 'failed' },
							},
						},
						done: {},
						failed: {},
					},
				},
			},
		},
	},
});
